import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, Loader2, Pencil, Plus, RotateCcw, Trash2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { CategoryManager } from '../components/CategoryManager';
import { DataTable } from '../components/DataTable';
import { Filters, useFilters, type FilterField } from '../components/Filters';
import { EmptyState } from '../components/EmptyState';
import { SelectionBar, type BulkAction } from '../components/SelectionBar';
import type { CsvColumn } from '../lib/csv';
import type { InventoryList } from '../lib/types';
import { HelpHint } from '../components/HelpHint';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';

/**
 * The stock list — what this business fits, and what it carries to fit it.
 *
 * ## Why the list has two halves
 *
 * The built-in half is a fixed set in the domain package, and the reasoning
 * for it still holds: a drum of fibre logged as "fibre", "Fiber 100m", "cable"
 * and "drop wire" is four things no report can add up, and unaccounted stock
 * is the quietest way an ISP loses money. Those codes are what job sheets and
 * movements years old are keyed on, so they cannot be renamed or removed here
 * — a rename would silently re-label history.
 *
 * The custom half is what that list could never do: grow. A technician who
 * fits something the list has never heard of had two options, and both were
 * bad — the free-text "other" box, which is reviewed and never counted, or the
 * nearest wrong code. Now they can add it.
 *
 * ## Why anybody who can see it can edit it
 *
 * Because the person who finds out an item is missing is the technician
 * standing in front of it, at a cabinet, with the job half done. Waiting for
 * an administrator means the free-text box, which means the stock stops adding
 * up. Two things nobody can do at any level, because both rewrite what records
 * already in the ledger mean: edit a built-in item, and change the unit of an
 * item that has already been counted somewhere.
 */
/**
 * What each level looks like, and why colour is not the only signal.
 *
 * Every row also carries the words — "Running low", "None left" — because
 * colour alone fails for the readers who need it most: about one man in twelve
 * cannot reliably separate red from green, and a warehouse is often read on a
 * phone in daylight. The colour makes the list scannable; the words make it
 * legible.
 *
 * `unwatched` is grey rather than green on purpose. No reorder level means
 * nothing here can judge the quantity, and a green tick would be a claim this
 * screen is not entitled to make — an item left blank is never reported as low,
 * so it runs out silently.
 */
const LEVEL_WORD: Record<string, string> = {
  out: 'none left',
  low: 'running low',
  ok: 'enough',
  high: 'more than needed',
  unwatched: 'no reorder level',
};

const LEVEL_STYLE: Record<string, string> = {
  out: 'bg-danger-50 text-danger-700 border-danger-200',
  low: 'bg-accent-50 text-accent-800 border-accent-200',
  ok: 'bg-secondary-50 text-secondary-900 border-secondary-200',
  high: 'bg-primary-50 text-primary-800 border-primary-200',
  unwatched: 'bg-muted text-muted-foreground border-border',
};

/** Cents to a readable figure. The list is scanned, so no decimals. */
const money = (cents: number | null) =>
  cents === null ? null : `KES ${Math.round(cents / 100).toLocaleString('en-KE')}`;

/** One row of the stock list, as this screen works with it. */
type StockRow = InventoryList['custom'][number] & { builtIn: boolean };

export function InventoryListPage() {
  const { can } = useSession();
  const mayEdit = can('stock:edit_list');
  const queryClient = useQueryClient();

  const [adding, setAdding] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [draft, setDraft] = useState({
    code: '',
    name: '',
    aka: '',
    category: '',
    unit: 'each',
    requiresSerial: false,
    maxPerVisit: '',
  });

  const list = useQuery({ queryKey: ['inventory-items'], queryFn: api.inventoryItems });

  /**
   * A short code from a name: "Drop wire clamp" becomes DROP_WIRE_CLAMP.
   *
   * Nobody should have to invent one. The code only has to be stable, unique
   * and recognisable a year later in a spreadsheet column, and the name it was
   * added under satisfies all three — so the form proposes it and gets out of
   * the way. It stops proposing the moment the code is typed in by hand.
   */
  const suggestCode = (name: string) =>
    name
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 32);

  const [codeTouched, setCodeTouched] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState({
    name: '',
    aka: '',
    category: '',
    unit: 'each',
    requiresSerial: false,
    maxPerVisit: '',
  });
  const [showTrash, setShowTrash] = useState(false);
  /** Ticked rows, keyed by code — stable, and what the CSV round-trips on. */
  const [chosen, setChosen] = useState<Set<string>>(new Set());

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['inventory-items'] });
    // The pickers read the catalogue, so they have to hear about this too.
    void queryClient.invalidateQueries({ queryKey: ['stock-catalogue'] });
    void queryClient.invalidateQueries({ queryKey: ['material-catalogue'] });
  };

  const add = useMutation({
    mutationFn: () =>
      api.addInventoryItem({
        code: draft.code.trim(),
        name: draft.name.trim(),
        ...(draft.aka.trim() ? { aka: draft.aka.trim() } : {}),
        category: draft.category,
        unit: draft.unit,
        // A length off a drum has no sticker. The box is disabled for measured
        // items, but the unit can be changed after it was ticked.
        requiresSerial: draft.unit === 'metre' ? false : draft.requiresSerial,
        ...(draft.maxPerVisit.trim() ? { maxPerVisit: Number(draft.maxPerVisit) } : {}),
      }),
    onSuccess: (item) => {
      setAdding(false);
      setCodeTouched(false);
      setDraft({
        code: '',
        name: '',
        aka: '',
        category: '',
        unit: 'each',
        requiresSerial: false,
        maxPerVisit: '',
      });
      setProblem(null);
      setOutcome(`${item.name} added as ${item.code}.`);
      refresh();
    },
    onError: (error) =>
      setProblem(error instanceof ApiError ? error.message : 'Could not add that item.'),
  });

  const edit = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Parameters<typeof api.editInventoryItem>[1] }) =>
      api.editInventoryItem(id, patch),
    onSuccess: (item) => {
      setProblem(null);
      setOutcome(`${item.name} ${item.isActive ? 'is back on the list' : 'is retired'}.`);
      refresh();
    },
    onError: (error) =>
      setProblem(error instanceof ApiError ? error.message : 'Could not change that item.'),
  });

  /*
   * Retired or deleted is the server's decision, not this screen's: an item
   * already counted somewhere cannot be deleted without leaving those records
   * naming nothing. The message comes back with the answer so the screen
   * cannot claim a deletion that did not happen.
   */
  const remove = useMutation({
    mutationFn: (id: string) => api.removeInventoryItem(id),
    onSuccess: (result) => {
      setProblem(null);
      setOutcome(result.message);
      refresh();
    },
    onError: (error) =>
      setProblem(error instanceof ApiError ? error.message : 'Could not remove that item.'),
  });

  const restore = useMutation({
    mutationFn: (id: string) => api.restoreInventoryItem(id),
    onSuccess: (result) => {
      setProblem(null);
      setOutcome(result.message);
      refresh();
    },
    onError: (error) =>
      setProblem(error instanceof ApiError ? error.message : 'Could not restore that item.'),
  });

  /*
   * Memoised because the grouping below depends on them, and `?? {}` makes a
   * fresh object on every render — which would regroup the whole list on every
   * keystroke in the add form.
   */
  const categories = useMemo(() => list.data?.categories ?? {}, [list.data]);
  const trash = useMemo(() => list.data?.trash ?? [], [list.data]);
  const categoryHelp = useMemo(() => list.data?.categoryHelp ?? {}, [list.data]);
  const units = useMemo(() => list.data?.units ?? {}, [list.data]);
  const unitHelp = useMemo(() => list.data?.unitHelp ?? {}, [list.data]);

  /*
   * A measured item is a different question in three places — the ceiling is
   * in metres, the serial box makes no sense, and the hints have to say so.
   */
  const measured = draft.unit === 'metre';

  /**
   * What is still needed, in the words used on the form.
   *
   * This exists because "Add it" used to be `disabled` until all three were
   * filled, and a greyed-out button is the worst possible answer to "why is
   * nothing happening" — it does not say what is wrong, it does not say which
   * field, and pressing it, which is the only thing anybody tries, does
   * nothing at all. It was reported exactly that way: "the submit button is
   * not adding anything."
   *
   * Category was always the one people hit. The code fills itself in from the
   * name, so two of the three are satisfied by typing, and the one left is a
   * dropdown that looks optional because every other dropdown on the form has
   * a value in it already.
   */
  /**
   * The item this code already belongs to, if any.
   *
   * The server refuses a duplicate and says so clearly — "CLAMP is already on
   * the built-in list as Clamp / tension set" — but only once the form has been
   * filled in and submitted, and the collision is easiest to hit precisely
   * because the code now fills itself in: type "Clamp" and you get CLAMP, which
   * is a built-in. Finding that out after pressing Add is finding it out one
   * step too late, so the answer is on screen while the code is being typed.
   *
   * One list to search. A seeded code and a code somebody typed collide in
   * exactly the same way, and to the person typing they are the same problem:
   * the thing they are adding is already there.
   *
   * The trash is searched too. A code in the trash is still taken — restoring
   * is one press, and a second item with the same code would make one stored
   * value mean two things.
   */
  const clash = useMemo(() => {
    const code = draft.code.trim().toUpperCase();
    if (code.length < 2 || !list.data) return null;

    const own = list.data.custom.find((item) => item.code === code);
    if (own) return { name: own.name, builtIn: own.isBuiltIn, inTrash: false };

    const binned = list.data.trash.find((item) => item.code === code);
    return binned ? { name: binned.name, builtIn: false, inTrash: true } : null;
  }, [draft.code, list.data]);

  const missing = [
    draft.name.trim().length < 2 ? 'a name' : null,
    draft.code.trim().length < 2 ? 'a short code' : null,
    !draft.category ? 'a category' : null,
  ].filter(Boolean) as string[];

  const sayWhatIsMissing = () =>
    setProblem(
      missing.length === 1
        ? `This needs ${missing[0]} before it can be added.`
        : `This needs ${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]} before it can be added.`,
    );

  /**
   * The whole list, flat.
   *
   * It used to be grouped into a card per category, which read well and could
   * not be sorted, searched or selected across. The table does the grouping
   * now — sort by Category and you have the old view back, and every other
   * ordering as well.
   *
   * The built-ins used to arrive as a second array with no id, which is exactly
   * why nothing could offer to edit them: there was nothing to address. They
   * are rows now, and `builtIn` is a badge rather than a rule.
   */
  const all = useMemo(
    () => (list.data?.custom ?? []).map((item) => ({ ...item, builtIn: item.isBuiltIn })),
    [list.data],
  );

  const editing = useMemo(
    () => all.find((row) => row.id === editingId) ?? null,
    [all, editingId],
  );

  const FIELDS: FilterField<StockRow>[] = useMemo(
    () => [
      { key: 'name', label: 'Item', type: 'text', value: (row) => row.name },
      { key: 'aka', label: 'Also called', type: 'text', value: (row) => row.aka },
      { key: 'code', label: 'Code', type: 'text', value: (row) => row.code },
      {
        key: 'category',
        label: 'Category',
        type: 'choice',
        options: Object.entries(categories).map(([value, label]) => ({ value, label })),
        value: (row) => row.category,
      },
      { key: 'onHand', label: 'On hand', type: 'number', value: (row) => row.onHand },
      {
        key: 'level',
        label: 'Stock level',
        type: 'choice',
        options: [
          { value: 'out', label: 'None left' },
          { value: 'low', label: 'Running low' },
          { value: 'ok', label: 'Enough' },
          { value: 'high', label: 'More than needed' },
          { value: 'unwatched', label: 'No reorder level' },
        ],
        value: (row) => row.level,
      },
      {
        key: 'unit',
        label: 'Unit',
        type: 'choice',
        options: [
          { value: 'each', label: 'Counted' },
          { value: 'metre', label: 'Measured' },
        ],
        value: (row) => row.unit,
      },
      {
        key: 'serial',
        label: 'Needs a serial',
        type: 'choice',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        value: (row) => (row.requiresSerial ? 'yes' : 'no'),
      },
      {
        key: 'active',
        label: 'In use',
        type: 'choice',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'Retired' },
        ],
        value: (row) => (row.isActive ? 'yes' : 'no'),
      },
      { key: 'added', label: 'Added', type: 'date', value: (row) => row.createdAt },
    ],
    [categories],
  );

  const { query, setQuery, clauses, setClauses, filtered: rows } = useFilters(all, FIELDS);

  /**
   * What a spreadsheet of this list looks like.
   *
   * The headings are the words on the screen rather than the property names,
   * because the file is opened by a person in Excel — and they are what an
   * import matches on, so the file this exports is the file it accepts back.
   */
  const CSV_COLUMNS: CsvColumn<StockRow>[] = [
    { header: 'Code', value: (row) => row.code },
    { header: 'Item', value: (row) => row.name },
    { header: 'Also called', value: (row) => row.aka ?? '' },
    { header: 'Category', value: (row) => categories[row.category] ?? row.category },
    { header: 'Unit', value: (row) => row.unit },
    { header: 'On hand', value: (row) => row.onHand },
    { header: 'Worth', value: (row) => (row.valueCents === null ? '' : row.valueCents / 100) },
    { header: 'Serial', value: (row) => (row.requiresSerial ? 'yes' : 'no') },
    { header: 'Quantity', value: (row) => row.maxPerVisit ?? '' },
    { header: 'In use', value: (row) => (row.isActive ? 'yes' : 'no') },
  ];

  /** Label back to key, so an import can speak the language of the export. */
  const categoryKeyFor = (label: string) => {
    const wanted = label.trim().toLowerCase();
    const hit = Object.entries(categories).find(
      ([key, name]) => name.toLowerCase() === wanted || key === wanted,
    );
    return hit?.[0] ?? null;
  };

  /**
   * A spreadsheet back in.
   *
   * Matched on `Code`, because that is the join key everywhere else — a row
   * whose code exists is a correction, and one whose code does not is a new
   * item. Quantities are deliberately **not** imported: a balance is a sum
   * over the ledger, and a spreadsheet overwriting it would leave every
   * movement disagreeing with the figure with nothing to say which was right.
   */
  const importItems = useMutation({
    mutationFn: async (records: Array<Record<string, string>>) => {
      const known = new Map(rows.map((row) => [row.code, row]));
      let added = 0;
      let corrected = 0;
      const refused: string[] = [];

      for (const record of records) {
        const code = (record.Code ?? record.code ?? '').trim().toUpperCase();
        const name = (record.Item ?? record.Name ?? record.name ?? '').trim();
        if (!code || !name) {
          refused.push(`${code || '(no code)'} — needs a Code and an Item`);
          continue;
        }

        const category = categoryKeyFor(record.Category ?? record.category ?? '');
        if (!category) {
          refused.push(`${code} — "${record.Category ?? ''}" is not a category on this list`);
          continue;
        }

        const aka = (record['Also called'] ?? '').trim();
        const serial = /^(yes|true|1|required)$/i.test((record.Serial ?? '').trim());
        const max = Number((record.Quantity ?? '').trim());
        const unit = /metre/i.test((record.Unit ?? '').trim()) ? 'metre' : 'each';

        try {
          const existing = known.get(code);
          if (existing?.id) {
            await api.editInventoryItem(existing.id, {
              name,
              aka,
              category,
              requiresSerial: unit === 'metre' ? false : serial,
              ...(Number.isFinite(max) && max > 0 ? { maxPerVisit: max } : {}),
            });
            corrected += 1;
          } else {
            await api.addInventoryItem({
              code,
              name,
              ...(aka ? { aka } : {}),
              category,
              unit,
              requiresSerial: unit === 'metre' ? false : serial,
              ...(Number.isFinite(max) && max > 0 ? { maxPerVisit: max } : {}),
            });
            added += 1;
          }
        } catch (error) {
          refused.push(`${code} — ${error instanceof ApiError ? error.message : 'could not be saved'}`);
        }
      }

      return { added, corrected, refused };
    },
    onSuccess: (result) => {
      setProblem(result.refused.length > 0 ? result.refused.slice(0, 4).join(' · ') : null);
      setOutcome(
        `${result.added} added, ${result.corrected} corrected` +
          (result.refused.length > 0 ? `, ${result.refused.length} refused.` : '.'),
      );
      setChosen(new Set());
      refresh();
    },
    onError: (error) =>
      setProblem(error instanceof ApiError ? error.message : 'That file could not be read.'),
  });

  /**
   * What can be done to several at once.
   *
   * Every one of these is something somebody would otherwise do forty times by
   * hand, and none of them destroys anything: the trash is recoverable and
   * retiring is a toggle.
   */
  const bulkActions: BulkAction<StockRow>[] = [
    {
      key: 'print',
      label: 'Print',
      prominent: true,
      /*
       * The browser's own print, against a stylesheet that drops the controls.
       * A separate print view would be a second copy of this table to keep in
       * step, and the one that drifts is always the one nobody looks at.
       */
      run: () => window.print(),
    },
    {
      key: 'retire',
      label: 'Retire',
      confirm: (picked) =>
        `Retire ${picked.length} item${picked.length === 1 ? '' : 's'}? ` +
        'They stay on the list and stop being offered on new work.',
      run: async (picked) => {
        for (const row of picked) {
          if (row.id) await api.editInventoryItem(row.id, { isActive: false });
        }
        setOutcome(`${picked.length} retired. They are still on the list.`);
        setChosen(new Set());
        refresh();
      },
    },
    {
      key: 'reinstate',
      label: 'Put back in use',
      run: async (picked) => {
        for (const row of picked) {
          if (row.id) await api.editInventoryItem(row.id, { isActive: true });
        }
        setOutcome(`${picked.length} back in use.`);
        setChosen(new Set());
        refresh();
      },
    },
    {
      key: 'trash',
      label: 'Move to the trash',
      destructive: true,
      confirm: (picked) =>
        `Move ${picked.length} item${picked.length === 1 ? '' : 's'} to the trash? ` +
        'Nothing is destroyed — you can restore them from the trash.',
      run: async (picked) => {
        for (const row of picked) {
          if (row.id) await api.removeInventoryItem(row.id);
        }
        setOutcome(`${picked.length} in the trash. Nothing was deleted.`);
        setChosen(new Set());
        refresh();
      },
    },
  ];

  if (list.isPending) {
    return <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin text-primary-500" />;
  }

  if (list.isError) {
    return (
      <p className="card text-sm text-muted-foreground">
        The stock list could not be loaded. Reload the page, and if it keeps happening tell an
        administrator.
      </p>
    );
  }

  const customCount = list.data?.custom.length ?? 0;
  const builtInCount = list.data?.builtIn.length ?? 0;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-1.5 font-heading text-2xl">
            <Boxes className="h-6 w-6 text-primary-600" aria-hidden="true" />
            Stock list
            <HelpHint>
              Everything a technician can record fitting or using. What is on this list is what a
              report can add up — anything typed into the “other” box instead is reviewed by hand
              and never counted, which is why adding the item is worth the minute it takes.
            </HelpHint>
          </h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {builtInCount} built-in {builtInCount === 1 ? 'item' : 'items'} and {customCount} of
            your own
            {!mayEdit && ' · read-only for your role'}
          </p>
        </div>

        {mayEdit && !adding && (
          <button type="button" className="btn-primary" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add an item
          </button>
        )}
      </div>

      {/*
        * The categories, above the items.
        *
        * Above, because the question "where does this go" comes before "what
        * is it called", and because somebody who cannot find a category for
        * what they are holding needs to see that adding one is allowed before
        * they give up and use the nearest wrong one.
        */}
      <CategoryManager />

      {outcome && (
        <p className="card flex items-start justify-between gap-3 border-secondary-300 bg-secondary-50 text-sm text-secondary-900">
          <span>{outcome}</span>
          <button type="button" onClick={() => setOutcome(null)} aria-label="Dismiss">
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </p>
      )}

      {problem && (
        <p className="card border-accent-300 bg-accent-50 text-sm text-accent-900">{problem}</p>
      )}

      {adding && (
        <section className="card space-y-3">
          <h3 className="font-heading text-lg">Add an item</h3>
          <p className="text-sm text-muted-foreground">
            The short code is what every movement and job sheet stores, so it cannot be changed
            afterwards. Everything else can.{' '}
            <span className="whitespace-nowrap">
              <span className="text-accent-600">*</span> is needed before it can be added.
            </span>
          </p>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <label className="block text-sm">
              <span className="mb-1 flex items-center gap-1 font-medium">
                Short code
                <span className="text-accent-600" aria-hidden="true">
                  *
                </span>
                <span className="sr-only">(required)</span>
                <HelpHint>
                  <strong>You do not have to invent one — type the name first and this fills
                  itself in.</strong>{' '}
                  It is the name in capitals with underscores instead of spaces: “Drop wire clamp”
                  becomes DROP_WIRE_CLAMP. Change it if you prefer something shorter.
                  <br />
                  <br />
                  What it is for: every movement, job sheet and stock report stores this code
                  rather than the name, so it is how the same item is recognised across years of
                  records. That is also why it cannot be changed after saving — changing it would
                  make old records point at nothing. The name can be changed whenever you like.
                  <br />
                  <br />
                  Rules: capital letters, digits and underscores only, and it must not already be
                  in use. If two things are nearly the same, put the difference in the code —
                  CLAMP_DROP and CLAMP_ADSS, not CLAMP and CLAMP2.
                </HelpHint>
              </span>
              <input
                className="input w-full font-mono uppercase"
                value={draft.code}
                onChange={(event) => {
                  setCodeTouched(true);
                  setDraft((old) => ({ ...old, code: event.target.value }));
                }}
                placeholder="DROP_WIRE_CLAMP"
              />
              {clash ? (
                <span className="mt-1 block text-xs text-accent-700">
                  Already taken —{' '}
                  {clash.inTrash ? 'in the trash' : clash.builtIn ? 'built in' : 'on your list'} as
                  “{clash.name}”. {clash.inTrash ? 'Restore it' : 'Use that one'}, or change this
                  code.
                </span>
              ) : (
                <span className="mt-1 block text-xs text-muted-foreground">
                  Letters, digits and underscores. Fixed once saved.
                </span>
              )}
            </label>

            <label className="block text-sm">
              <span className="mb-1 flex items-center gap-1 font-medium">
                Name
                <span className="text-accent-600" aria-hidden="true">
                  *
                </span>
                <span className="sr-only">(required)</span>
                <HelpHint>
                  What this is called when you order it or read it off the box — the full, plain
                  name. “Drop wire clamp”, not “clamp”.
                  <br />
                  <br />
                  This is what a technician sees in the picker on a job, so write it the way
                  somebody standing at a cabinet would look for it. It can be corrected later
                  without affecting any record.
                </HelpHint>
              </span>
              <input
                className="input w-full"
                value={draft.name}
                onChange={(event) => {
                  const name = event.target.value;
                  setDraft((old) => ({
                    ...old,
                    name,
                    code: codeTouched ? old.code : suggestCode(name),
                  }));
                }}
                placeholder="Drop wire clamp"
              />
              <span className="mt-1 block text-xs text-muted-foreground">
                The full name, as you would order it.
              </span>
            </label>

            <label className="block text-sm">
              <span className="mb-1 flex items-center gap-1 font-medium">
                Also called
                <HelpHint>
                  The nickname — what the crews actually say on site, or what the supplier prints
                  on the invoice when it differs from the name above.
                  <br />
                  <br />
                  It exists so searching finds the item either way: type “P-clamp”, “tension
                  clamp” or “anchor” and the same thing comes up. If a job sheet keeps coming back
                  with a word in the “other” box, that word belongs here.
                  <br />
                  <br />
                  Leave it empty if there is only ever one name for it. Put several in separated
                  by commas.
                </HelpHint>
              </span>
              <input
                className="input w-full"
                value={draft.aka}
                onChange={(event) => setDraft((old) => ({ ...old, aka: event.target.value }))}
                placeholder="P-clamp, tension clamp"
              />
              <span className="mt-1 block text-xs text-muted-foreground">
                Optional. The site name, so searching finds it either way.
              </span>
            </label>

            <label className="block text-sm">
              <span className="mb-1 flex items-center gap-1 font-medium">
                Category
                <span className="text-accent-600" aria-hidden="true">
                  *
                </span>
                <span className="sr-only">(required)</span>
                <HelpHint>
                  How it is grouped on the technician’s checklist and in every stock report.
                  <br />
                  <br />
                  Pick by what the thing is <em>for</em>, not what it is made of: a steel bracket
                  that holds a cable on a pole is aerial hardware, not consumables. When two fit,
                  pick the one you would want it counted under at the end of the month.
                  <ul className="mt-2 space-y-1">
                    {Object.entries(categories).map(([value, label]) => (
                      <li key={value}>
                        <strong>{label}</strong>
                        {categoryHelp[value] ? ` — ${categoryHelp[value]}` : ''}
                      </li>
                    ))}
                  </ul>
                </HelpHint>
              </span>
              <select
                className="input w-full"
                value={draft.category}
                onChange={(event) => setDraft((old) => ({ ...old, category: event.target.value }))}
              >
                <option value="">Choose a category…</option>
                {Object.entries(categories).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-xs text-muted-foreground">
                {draft.category && categoryHelp[draft.category]
                  ? categoryHelp[draft.category]
                  : 'How it is grouped on the technician’s checklist.'}
              </span>
            </label>

            <label className="block text-sm">
              <span className="mb-1 flex items-center gap-1 font-medium">
                Counted or measured
                <HelpHint>
                  Whether a technician records <em>how many</em> or <em>how far</em>.
                  <br />
                  <br />
                  <strong>Counted (each)</strong> — whole things you can put in a box. Routers,
                  clamps, closures, poles. The technician types a whole number: 1, 2, 14.
                  <br />
                  <br />
                  <strong>Measured (metres)</strong> — anything that comes off a drum or a roll and
                  is cut to fit. Drop wire, ADSS, conduit, earth wire, draw rope. The technician
                  types the length used on that job — 85 for eighty-five metres — and the stock
                  figure goes down by that many metres. You do not enter any length here; this only
                  tells the app which question to ask.
                  <br />
                  <br />
                  Pick carefully: this one cannot be changed once the item has been counted
                  anywhere, because it would change what every earlier number meant. Half a router
                  is a typo; half a metre is a Tuesday.
                </HelpHint>
              </span>
              <select
                className="input w-full"
                value={draft.unit}
                onChange={(event) => setDraft((old) => ({ ...old, unit: event.target.value }))}
              >
                {Object.entries(units).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-xs text-muted-foreground">
                {unitHelp[draft.unit] ??
                  'Cable is measured. Everything else is counted — half a router is a typo.'}
              </span>
            </label>

            <label className="block text-sm">
              <span className="mb-1 flex items-center gap-1 font-medium">
                Quantity
                <HelpHint>
                  Optional. The largest sensible amount for one job — a ceiling, not a target.
                  <br />
                  <br />
                  If a technician records more than this on a single visit the app queries it
                  before saving. It is there to catch the slipped finger: a 1000 where 100 was
                  meant, {measured ? 'or a drum-and-a-half of fibre on a flat' : 'or ten routers in a one-router house'}.
                  <br />
                  <br />
                  {measured
                    ? 'In metres, because this item is measured. A 300 metre ceiling on drop wire catches the typo and still allows the long runs.'
                    : 'A whole number. Leave it empty if there is no sensible ceiling — spare fixings, cable ties.'}
                  <br />
                  <br />
                  Leaving it empty is fine and changes nothing except that nothing is queried.
                </HelpHint>
              </span>
              <input
                className="input w-full tabular-nums"
                type="number"
                inputMode="numeric"
                min={1}
                value={draft.maxPerVisit}
                onChange={(event) =>
                  setDraft((old) => ({ ...old, maxPerVisit: event.target.value }))
                }
                placeholder="no ceiling"
              />
              <span className="mt-1 block text-xs text-muted-foreground">
                The most that can go on one visit{measured ? ', in metres' : ''}. Optional —
                it catches a typed 1000.
              </span>
            </label>
          </div>

          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={draft.requiresSerial}
              onChange={(event) =>
                setDraft((old) => ({ ...old, requiresSerial: event.target.checked }))
              }
              disabled={measured}
            />
            <span>
              <span className="flex items-center gap-1 font-medium">
                Needs a serial number
                <HelpHint>
                  Tick this and the technician cannot finish the job without typing the serial off
                  the label — so only tick it for things that actually carry one.
                  <br />
                  <br />
                  <strong>Tick it for:</strong> anything with a unique number on a sticker and a
                  resale value — ONTs, routers, switches, media converters, test meters. The serial
                  is how a device that goes missing is traced back to the job it left on, and how a
                  warranty claim is made a year later without anybody guessing.
                  <br />
                  <br />
                  <strong>Leave it off for:</strong> anything identical to the next one out of the
                  box — clamps, closures, ties, pigtails — and anything measured, which has no
                  serial to read.
                  <br />
                  <br />
                  If in doubt, look at the item: if there is a barcode with a number under it that
                  differs from the one next to it, tick it.
                </HelpHint>
              </span>
              <span className="mt-0.5 block text-xs text-muted-foreground">
                {measured
                  ? 'Not available for measured items — a length off a drum has no serial.'
                  : 'For anything with a unique number on a sticker and a resale value.'}
              </span>
            </span>
          </label>

          <div className="flex flex-wrap gap-2 border-t border-border pt-3">
            {/*
              * Disabled only while the request is in flight, which is the one
              * case where pressing it again is genuinely wrong. Every other
              * time it is pressable and answers.
              */}
            <button
              type="button"
              className="btn-primary"
              disabled={add.isPending}
              onClick={() => {
                if (missing.length > 0) return sayWhatIsMissing();
                if (clash) {
                  return setProblem(
                    clash.inTrash
                      ? `That code belongs to “${clash.name}”, which is in the trash. Restore it, or give this a different code.`
                      : `That code is already on your list as “${clash.name}”. Use that one, or give this a different code.`,
                  );
                }
                return add.mutate();
              }}
            >
              {add.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Plus className="h-4 w-4" aria-hidden="true" />
              )}
              Add it
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setAdding(false);
                setProblem(null);
              }}
            >
              Cancel
            </button>
          </div>
        </section>
      )}

      {/*
        * The trash.
        *
        * A separate view rather than greyed rows in the list, because the two
        * are read for different reasons: the list answers "what can a
        * technician record", and this answers "what did somebody throw away".
        * Mixing them makes the first question harder to answer, which is the
        * one asked forty times a day.
        */}
      {trash.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={() => setShowTrash((was) => !was)}
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            {showTrash ? `Back to the list` : `Trash (${trash.length})`}
          </button>
          {showTrash && (
            <span className="text-xs text-muted-foreground">
              Nothing here was destroyed. Restore puts it back on the list, retired.
            </span>
          )}
        </div>
      )}

      {showTrash ? (
        <section className="card">
          <h3 className="font-heading text-lg">Trash</h3>
          <ul className="mt-2 divide-y divide-border">
            {trash.map((item) => (
              <li key={item.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                <div className="min-w-0">
                  <p className="font-medium">
                    {item.name}
                    <span className="ml-2 font-mono text-xs text-muted-foreground">
                      {item.code}
                    </span>
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {categories[item.category] ?? item.category}
                    {item.deletedByLabel ? ` · removed by ${item.deletedByLabel}` : ''}
                    {item.deletedAt
                      ? ` · ${new Date(item.deletedAt).toLocaleDateString('en-KE')}`
                      : ''}
                  </p>
                </div>
                {mayEdit && (
                  <button
                    type="button"
                    className="btn-secondary py-1.5 text-sm"
                    title={`Put ${item.name} back on the list`}
                    disabled={restore.isPending}
                    onClick={() => restore.mutate(item.id)}
                  >
                    <RotateCcw className="h-4 w-4" aria-hidden="true" />
                    Restore
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : (
        <>
          {/*
            * What the colours mean, said in words once.
            *
            * A legend is the difference between a colour that informs and a
            * colour that decorates. Without it the first question anybody asks
            * about a coloured list is what the colours mean, and they ask a
            * person rather than the screen.
            */}
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground print:hidden">
            <span className="font-medium text-foreground">What the colours mean:</span>
            {(['out', 'low', 'ok', 'high', 'unwatched'] as const).map((level) => (
              <span key={level} className="inline-flex items-center gap-1">
                <span
                  className={`inline-block h-2.5 w-2.5 rounded-full border ${LEVEL_STYLE[level]}`}
                  aria-hidden="true"
                />
                {LEVEL_WORD[level]}
              </span>
            ))}
            <span className="inline-flex items-center gap-1">
              <span className="rounded bg-secondary-100 px-1.5 py-0.5 text-secondary-900">new</span>
              added in the last 30 days
            </span>
          </p>

          {/*
            * Editing happens above the table rather than inside it.
            *
            * A row that turns into a form is the wrong shape here: the form has
            * six fields and the row has eight columns, so it either squeezes or
            * spans the lot and the table stops being a table. Above it, the row
            * stays where it was and the eye does not have to find it again.
            */}
          {editing && (
            <section className="card space-y-3 border-primary-300 print:hidden">
              <h3 className="font-heading text-lg">
                Editing {editing.name}
                <span className="ml-2 font-mono text-xs text-muted-foreground">{editing.code}</span>
              </h3>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <label className="block text-sm">
                  <span className="mb-1 block font-medium">Name</span>
                  <input
                    className="input w-full"
                    value={editDraft.name}
                    aria-label={`Name for ${editing.name}`}
                    onChange={(event) =>
                      setEditDraft((old) => ({ ...old, name: event.target.value }))
                    }
                  />
                </label>

                <label className="block text-sm">
                  <span className="mb-1 block font-medium">Also called</span>
                  <input
                    className="input w-full"
                    value={editDraft.aka}
                    aria-label={`Also called, for ${editing.name}`}
                    onChange={(event) =>
                      setEditDraft((old) => ({ ...old, aka: event.target.value }))
                    }
                  />
                </label>

                <label className="block text-sm">
                  <span className="mb-1 block font-medium">Category</span>
                  <select
                    className="input w-full"
                    value={editDraft.category}
                    aria-label={`Category for ${editing.name}`}
                    onChange={(event) =>
                      setEditDraft((old) => ({ ...old, category: event.target.value }))
                    }
                  >
                    {Object.entries(categories).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>

                {/*
                  * The unit, editable until something has counted it.
                  *
                  * It was not offered at all, which made it look permanent — and
                  * it is not. Once anything has counted the item it is genuinely
                  * fixed, because forty counted is not forty metres and every
                  * movement already recorded would change meaning.
                  */}
                <label className="block text-sm">
                  <span className="mb-1 block font-medium">Counted or measured</span>
                  <select
                    className="input w-full"
                    value={editDraft.unit}
                    disabled={editing.timesUsed > 0}
                    aria-label={`Unit for ${editing.name}`}
                    onChange={(event) =>
                      setEditDraft((old) => ({ ...old, unit: event.target.value }))
                    }
                  >
                    {Object.entries(units).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <span className="mt-1 block text-xs text-muted-foreground">
                    {editing.timesUsed > 0
                      ? `Fixed — ${editing.timesUsed} record${editing.timesUsed === 1 ? '' : 's'} already count it this way.`
                      : 'Nothing has counted it yet, so this can still change.'}
                  </span>
                </label>

                <label className="block text-sm">
                  <span className="mb-1 block font-medium">Quantity</span>
                  <input
                    className="input w-full tabular-nums"
                    type="number"
                    min={1}
                    value={editDraft.maxPerVisit}
                    aria-label={`Quantity for ${editing.name}`}
                    placeholder="no ceiling"
                    onChange={(event) =>
                      setEditDraft((old) => ({ ...old, maxPerVisit: event.target.value }))
                    }
                  />
                  <span className="mt-1 block text-xs text-muted-foreground">
                    The most that can go on one visit
                    {editDraft.unit === 'metre' ? ', in metres' : ''}.
                  </span>
                </label>

                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={editDraft.unit === 'metre' ? false : editDraft.requiresSerial}
                    disabled={editDraft.unit === 'metre'}
                    onChange={(event) =>
                      setEditDraft((old) => ({ ...old, requiresSerial: event.target.checked }))
                    }
                  />
                  <span>
                    <span className="font-medium">Needs a serial number</span>
                    {editDraft.unit === 'metre' && (
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        Not available for a measured item — a length off a drum has no sticker.
                      </span>
                    )}
                  </span>
                </label>
              </div>

              <p className="text-xs text-muted-foreground">
                {editing.code} — the short code cannot change, because every movement and job sheet
                already stores it.
              </p>

              <div className="flex flex-wrap gap-2 border-t border-border pt-3">
                <button
                  type="button"
                  className="btn-primary"
                  disabled={edit.isPending}
                  onClick={() =>
                    editDraft.name.trim().length < 2
                      ? setProblem('An item needs a name of at least two characters.')
                      : edit.mutate({
                          id: editing.id!,
                          patch: {
                            name: editDraft.name.trim(),
                            aka: editDraft.aka.trim(),
                            category: editDraft.category,
                            ...(editing.timesUsed === 0 ? { unit: editDraft.unit } : {}),
                            requiresSerial:
                              editDraft.unit === 'metre' ? false : editDraft.requiresSerial,
                            ...(editDraft.maxPerVisit.trim()
                              ? { maxPerVisit: Number(editDraft.maxPerVisit) }
                              : {}),
                          },
                        })
                  }
                >
                  {edit.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                  Save
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setEditingId(null)}
                >
                  Cancel
                </button>
              </div>
            </section>
          )}

          <Filters<StockRow>
            fields={FIELDS}
            query={query}
            onQuery={setQuery}
            clauses={clauses}
            onClauses={setClauses}
            total={all.length}
            showing={rows.length}
            placeholder="Search by item, code or what it is also called"
          />

          <section className="card">
            <DataTable<StockRow>
              rows={rows}
              rowKey={(row) => row.code}
              caption={`${rows.length} ${rows.length === 1 ? 'item' : 'items'} on the stock list`}
              defaultSort={{ key: 'name' }}
              selected={mayEdit ? chosen : undefined}
              onSelect={mayEdit ? setChosen : undefined}
              empty={
                <EmptyState
                  icon={Boxes}
                  headline="Nothing on the list"
                  body="Add the first item, or reload if you expected some."
                />
              }
              columns={[
                {
                  key: 'name',
                  header: 'Item',
                  sortBy: (row) => row.name.toLowerCase(),
                  cell: (row) => (
                    <span>
                      {row.name}
                      {row.aka && <span className="text-muted-foreground"> · {row.aka}</span>}
                      {row.isNew && (
                        <span className="ml-2 rounded bg-secondary-100 px-1.5 py-0.5 text-xs text-secondary-900">
                          new
                        </span>
                      )}
                      {row.builtIn && (
                        <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                          built-in
                        </span>
                      )}
                      {!row.isActive && (
                        <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs">retired</span>
                      )}
                    </span>
                  ),
                },
                {
                  key: 'code',
                  header: 'Code',
                  sortBy: (row) => row.code,
                  cell: (row) => <span className="font-mono text-xs">{row.code}</span>,
                },
                {
                  key: 'category',
                  header: 'Category',
                  secondary: true,
                  sortBy: (row) => categories[row.category] ?? row.category,
                  cell: (row) => categories[row.category] ?? row.category,
                },
                {
                  key: 'onHand',
                  header: 'On hand',
                  numeric: true,
                  /*
                   * Sorted on the quantity, not on the badge. "Least first" is
                   * how somebody hunts for what has run out, and ordering the
                   * rendered text would sort 9 above 10.
                   */
                  sortBy: (row) => row.onHand,
                  cell: (row) => (
                    <span
                      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs tabular-nums ${LEVEL_STYLE[row.level]}`}
                      title={
                        row.reorderLevel === null
                          ? `${row.levelLabel} — set one under “When to reorder” and this can tell you when it runs low`
                          : `${row.levelLabel} — reorder level is ${row.reorderLevel}`
                      }
                    >
                      {row.onHand}
                      {row.unit === 'metre' ? 'm' : ''}
                    </span>
                  ),
                },
                {
                  key: 'worth',
                  header: 'Worth',
                  numeric: true,
                  secondary: true,
                  sortBy: (row) => row.valueCents,
                  cell: (row) => money(row.valueCents) ?? '—',
                },
                {
                  key: 'unit',
                  header: 'Unit',
                  secondary: true,
                  sortBy: (row) => row.unit,
                  cell: (row) => units[row.unit] ?? row.unit,
                },
                {
                  key: 'serial',
                  header: 'Serial',
                  secondary: true,
                  sortBy: (row) => (row.requiresSerial ? 0 : 1),
                  cell: (row) => (row.requiresSerial ? 'required' : '—'),
                },
                {
                  key: 'max',
                  header: 'Quantity',
                  numeric: true,
                  secondary: true,
                  sortBy: (row) => row.maxPerVisit,
                  cell: (row) => row.maxPerVisit ?? '—',
                },
                {
                  key: 'actions',
                  header: '',
                  cell: (row) =>
                    mayEdit && row.id ? (
                      <span className="inline-flex gap-1 print:hidden">
                        <button
                          type="button"
                          className="btn-ghost"
                          title={`Correct ${row.name}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            setEditingId(row.id!);
                            setEditDraft({
                              name: row.name,
                              aka: row.aka ?? '',
                              category: row.category,
                              unit: row.unit,
                              requiresSerial: row.requiresSerial,
                              maxPerVisit:
                                row.maxPerVisit === null ? '' : String(row.maxPerVisit),
                            });
                          }}
                        >
                          <Pencil className="h-4 w-4" aria-hidden="true" />
                          <span className="sr-only">Correct {row.name}</span>
                        </button>

                        <button
                          type="button"
                          className="btn-ghost"
                          title={
                            row.isActive
                              ? `Retire ${row.name} — it stays on the list and stops being offered`
                              : `Put ${row.name} back in use`
                          }
                          disabled={edit.isPending}
                          onClick={(event) => {
                            event.stopPropagation();
                            edit.mutate({ id: row.id!, patch: { isActive: !row.isActive } });
                          }}
                        >
                          <RotateCcw className="h-4 w-4" aria-hidden="true" />
                          <span className="sr-only">
                            {row.isActive ? 'Retire' : 'Reinstate'} {row.name}
                          </span>
                        </button>

                        <button
                          type="button"
                          className="btn-ghost text-danger-600"
                          title={`Move ${row.name} to the trash — nothing is destroyed and you can restore it`}
                          disabled={remove.isPending}
                          onClick={(event) => {
                            event.stopPropagation();
                            remove.mutate(row.id!);
                          }}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                          <span className="sr-only">Move {row.name} to the trash</span>
                        </button>
                      </span>
                    ) : null,
                },
              ]}
            />
          </section>

          {mayEdit && (
            <SelectionBar<StockRow>
              rows={rows}
              selected={chosen}
              onClear={() => setChosen(new Set())}
              rowKey={(row) => row.code}
              exportName="stock-list"
              csv={CSV_COLUMNS}
              onImport={(records) => importItems.mutateAsync(records)}
              actions={bulkActions}
            />
          )}
        </>
      )}
    </div>
  );
}
